import { Package, RegistrationAffiliation, RegistrationSession, Session } from "@/api/dtos";
import { ABSTRACT_VIEWING_SESSION_ID, DEFAULT_SESSION_GROUP, TRAINEE_AND_EARLY_CAREER_DAY_SESSION_ID, TRAINEE_AND_EARLY_CAREER_DINNER_SESSION_ID } from "@/constants/constants";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useScopedField } from "@ngt/forms-core";
import PackageContext from "@/contexts/PackageContext";
import Loading from "@/components/common/Loading";
import { ISession } from "..";
import SpeakerSessionGroup from "./SpeakerSessionGroup";
import { useIsCap, useIsSpeaker, useIsBoard, useIsTAndECSpeaker, useIsSponsor, useIsTrialCoordinator, useIsStaff, useRegistrationAffiliation } from "@/hooks/useUserRoles";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import { shouldExcludeFacultyDinner } from "./facultyDinnerEligibility";



interface ISessionsProps {
    packageInformation: Package,
    allSessions: Session[]
}

const useSelectedSessions = (allSessions: Session[]) => {

    // create a record of all sessiongs id mapped to their required group
    const sessionRequiredGroupMap = useMemo(() => {
        return allSessions.reduce<Record<number, string>>((currentState, currentItem) => {
            currentState[currentItem.id!] = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP;

            return currentState;
        }, {});
    }, [allSessions])

    const {state: { initialValue: initialRegistrationSessions, value: registrationSessions }, context: { setValue: setSelectedSessions }} = useScopedField<RegistrationSession[]>("sessions", {value: true, initialValue: true});

    // so basically the required group is never actually returned... da fuq

    const selectedSessions = useMemo(() => {
        return registrationSessions?.reduce<Record<number, [number, RegistrationSession]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [registrationSessions])

    const initialSessions = useMemo(() => {
        return initialRegistrationSessions?.reduce<Record<number, [number, RegistrationSession]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [initialRegistrationSessions])

    const updateSelectedSessions = useCallback((session: Session, selectedSession?: RegistrationSession, selectedSessionIndex?: number, included?: boolean, optional?: boolean) => 
    {
        const isOptional = session.required || optional;

        if (selectedSession && !included) {
            setSelectedSessions(current => {
                return current.filter(x =>
                    x.sessionId !== session.id
                    && (session.id !== TRAINEE_AND_EARLY_CAREER_DAY_SESSION_ID
                        || x.sessionId !== TRAINEE_AND_EARLY_CAREER_DINNER_SESSION_ID));
            })
        }
        else if (!selectedSession) {
            setSelectedSessions(current => {

                if (isOptional) {
                    current = current
                        .filter(x => sessionRequiredGroupMap[x.sessionId!] !== (session.requiredGroup ?? DEFAULT_SESSION_GROUP))
                }

                const newSession = new RegistrationSession({ 
                    sessionId: session.id
                });

                if (isOptional) {
                    sessionRequiredGroupMap[newSession.sessionId!] = session.requiredGroup  ?? DEFAULT_SESSION_GROUP;
                }

                if (current.some(x => x.sessionId === session.id)) {
                    return current;
                }

                current.push(newSession);

                return current;
            })
        }
    }, [setSelectedSessions, sessionRequiredGroupMap])

    return {initialSessions, selectedSessions: selectedSessions, updateSelectedSessions };
}

const useIncludedSessions = (packageInformation: Package) => {
    return useMemo(() => {
        const sessions = packageInformation.sessions?.filter(x => !x.required); // get all that are not required.. this will aactually get more then that

        if (!sessions || sessions.length === 0) {
            return undefined;
        }

        return sessions;
    }, [packageInformation.sessions])
}


const useIncludedOptionalSessions = (packageInformation: Package) => {
    return useMemo(() => {
        const sessions = packageInformation.sessions?.filter(x => x.required);

        if (!sessions || sessions.length === 0) {
            return undefined;
        }

        return sessions.reduce<Record<string, Session[]>>((currentState, currentItem) => {
            let groupKey = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP; // set the name of the variable in the object to the name in the database or default

            if (!currentState[groupKey]) { // if and empty object
                currentState[groupKey] = []; // set the empty objects groupkey field to an array
            }

            currentState[groupKey].push(currentItem); //push items into the array

            return currentState; // bring state back up to the top of loop untill looped through all
        }, {});
    }, [packageInformation.sessions])
}

const getOptionalSessions = (sessionsIn: Session[]) => {
    const sessions = sessionsIn.filter(x => x.requiredGroup && x.eventType === 3);

    if (!sessions || sessions.length === 0) {
        return undefined;
    }

    return sessions.reduce<Record<string, Session[]>>((currentState, currentItem) => {
        let groupKey = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP; // set the name of the variable in the object to the name in the database or default

        if (!currentState[groupKey]) { // if and empty object
            currentState[groupKey] = []; // set the empty objects groupkey field to an array
        }

        currentState[groupKey].push(currentItem); //push items into the array

        return currentState; // bring state back up to the top of loop untill looped through all
    }, {});
}

/**
 * 
 * @param additionalSessions This hook calculates the list of *additional* sessions, i.e. they are optional and selected through checkboxes
 * @param allSessions 
 * @param includedSessions 
 * @param isCap 
 * @param isSpeaker 
 * @param isTAndECSpeaker 
 * @param hasSpeakerPrivileges 
 * @param isBoard 
 * @returns 
 */
const useCalculatedAdditionalSessions = (
    additionalSessions: Session[] | undefined,
    allSessions: Session[],
    includedSessions: Session[] | undefined,
    isCap: boolean,
    isSpeaker: boolean,
    isTAndECSpeaker: boolean,
    hasSpeakerPrivileges: boolean,
    isBoard: boolean,
    registrationAffiliation?: RegistrationAffiliation | null
) => {
    return useMemo(() => {
        let list = additionalSessions?.filter(session => {

            if (session.requiredGroup) {
                return false;
            }

            //returning false from here means that it won't show in the list of optional sessions
            if (shouldExcludeFacultyDinner(session, { isCap, isTAndECSpeaker, registrationAffiliation })) {
                return false;
            }

            if (shouldAutoAddForSpeaker(session, isSpeaker, isBoard, isCap)) {
                return false;
            }

            if (shouldAutoAddForTAndECSpeaker(session, isTAndECSpeaker)) {
                return false;
            }

            if (shouldAutoAddForCap(session, isCap)) {
                return false;
            }

            return true;
        }) ?? [];

        return list;

    }, [additionalSessions, allSessions, includedSessions, isCap, isSpeaker, isTAndECSpeaker, hasSpeakerPrivileges, isBoard, registrationAffiliation]);
}

const convertToSessionList = (sessions: Session[], 
    selectedSessions: Record<number, [number, RegistrationSession]>, 
    initialSessions: Record<number, [number, RegistrationSession]>) => {
    return sessions.map(session => {
        const [selectedSessionIndex, selectedSession] = selectedSessions[session.id!] ?? [ undefined, undefined];
        const [initialSessionIndex, initialSession] = initialSessions[session.id!] ?? [ undefined, undefined];

        return {
            session,
            selectedSession,
            selectedSessionIndex,
            initialSession,
            initialSessionIndex
        } as ISession
    })
}

// Helper functions for session eligibility checks
const shouldExcludeAbstractViewingForTAndEC = (session: Session, isTAndECSpeaker: boolean): boolean => {
    return session.id === ABSTRACT_VIEWING_SESSION_ID && isTAndECSpeaker;
}

const shouldAutoAddForSpeaker = (session: Session, isSpeaker: boolean, isBoard: boolean, isCap: boolean): boolean => {
    return session.autoSetForSpeaker === true && (isSpeaker || isBoard || isCap);
}

const shouldAutoAddForTAndECSpeaker = (session: Session, isTAndECSpeaker: boolean): boolean => {
    return session.autoSetForTAndECSpeaker === true && isTAndECSpeaker;
}

//For things that are restricted to CAP, it forces them to be added.
//This only really affects the CAP panel.
const shouldAutoAddForCap = (session: Session, isCap: boolean): boolean => {
    return session.restrictToCap === true && isCap;
}

const SpeakerPackageSessions: FC<ISessionsProps> = ({
    packageInformation,
    allSessions
}) => {

    const {initialSessions, selectedSessions, updateSelectedSessions} = useSelectedSessions(allSessions);
    const includedSessions = useIncludedSessions(packageInformation);
    const includedOptionalSessions = useIncludedOptionalSessions(packageInformation);
    const { additionalSessions, additionalSessionsLoading } = useContext(PackageContext);

    const [optionalSessions, setOptionalSessions] = useState<Record<string, Session[]> | undefined>();

    useEffect(() => {
        if (additionalSessions) {
            setOptionalSessions(getOptionalSessions(additionalSessions));
        }
    }, [additionalSessions]);
    
    const sponsor = useIsSponsor();
    const trialCoordinator = useIsTrialCoordinator();
    const staff = useIsStaff();
    const isCap = useIsCap();
    const isBoard = useIsBoard();
    const isSpeaker = useIsSpeaker();
    const isTAndECSpeaker = useIsTAndECSpeaker();
    const registrationAffiliation = useRegistrationAffiliation();
    const isAdminSection = useIsAdminSection();
    const hasTraineeAndEarlyCareerDay = selectedSessions[TRAINEE_AND_EARLY_CAREER_DAY_SESSION_ID] != null
        || includedSessions?.some(session => session.id === TRAINEE_AND_EARLY_CAREER_DAY_SESSION_ID) === true;

    const {context: { setValue: setSelectedSessions }} = useScopedField<RegistrationSession[]>("sessions", {value: true});

    // Automatically add sessions with autoSetForSpeaker or autoSetForTAndECSpeaker to registration state
    // this is so the automatically included sessions get saved to the database too.
    useEffect(() => {
        if (!additionalSessions) {
            return;
        }

        const sessionsToAutoAdd: Session[] = [];

        additionalSessions.forEach(session => {
            // Skip abstract viewing for T&EC speakers
            if (shouldExcludeAbstractViewingForTAndEC(session, isTAndECSpeaker)) {
                return;
            }

            if (shouldExcludeFacultyDinner(session, { isCap, isTAndECSpeaker, registrationAffiliation })) {
                return;
            }

            // Check if this session should be auto-added for speaker/board/cap
            if (shouldAutoAddForSpeaker(session, isSpeaker, isBoard, isCap)) {
                sessionsToAutoAdd.push(session);
            }

            // Check if this session should be auto-added for T&EC speakers
            if (shouldAutoAddForTAndECSpeaker(session, isTAndECSpeaker)) {
                sessionsToAutoAdd.push(session);
            }

            // Check if this session should be auto-added for CAP users
            if (shouldAutoAddForCap(session, isCap)) {
                sessionsToAutoAdd.push(session);
            }
        });

        if (sessionsToAutoAdd.length > 0) {
            setSelectedSessions(current => {
                const updatedSessions = [...current];
                let hasChanges = false;

                sessionsToAutoAdd.forEach(session => {
                    // Only add if not already in the list
                    if (!updatedSessions.some(x => x.sessionId === session.id)) {
                        updatedSessions.push(new RegistrationSession({ sessionId: session.id }));
                        hasChanges = true;
                    }
                });

                return hasChanges ? updatedSessions : current;
            });
        }
    }, [additionalSessions, isSpeaker, isBoard, isCap, isTAndECSpeaker, registrationAffiliation, setSelectedSessions]);

    const includedSessionsUpdated = useMemo(() => {

        const additional = additionalSessions?.filter(session => {

            if (shouldExcludeAbstractViewingForTAndEC(session, isTAndECSpeaker)) {
                return false;
            }
            if (shouldExcludeFacultyDinner(session, { isCap, isTAndECSpeaker, registrationAffiliation })) {
                return false;
            }
            if (shouldAutoAddForSpeaker(session, isSpeaker, isBoard, isCap)) {
                return true;
            }
            if (shouldAutoAddForTAndECSpeaker(session, isTAndECSpeaker)) {
                return true;
            }
            if (shouldAutoAddForCap(session, isCap)) {
                return true;
            }

            return false;
        }) ?? [];

        //this is pretty hacky. the one cap session (consumer advisory panel meeting) is in allSessions, but not the package sessions
        //so we push it into the additional sessions array for cap members so later on it gets added to the mandatory sessions list and is removed from the optional list.
        if (isCap) {
            const capSessions = allSessions.filter(s => s.restrictToCap && !additionalSessions?.some(l => l.id === s.id) && !includedSessions?.some(is => is.id === s.id));
            
            if (capSessions.length > 0) {
                additional?.push(...capSessions);
            }
        }

        const combined = includedSessions?.concat(additional) ?? [];
        
        // Remove duplicates by session ID
        const seen = new Set<number>();
        return combined.filter(session => {
            if (seen.has(session.id!)) {
                return false;
            }
            seen.add(session.id!);
            return true;
        });
    }, [includedSessions, sponsor, trialCoordinator, staff, additionalSessions, allSessions, isBoard, isCap, isSpeaker, isTAndECSpeaker, registrationAffiliation]);

    const calculatedAdditionalSessions = useCalculatedAdditionalSessions(
        additionalSessions,
        allSessions,
        includedSessionsUpdated,
        isCap,
        isSpeaker,
        isTAndECSpeaker,
        isSpeaker,
        isBoard,
        registrationAffiliation
    );
    const additionalSessionsUpdated = useMemo(() => {
        return calculatedAdditionalSessions.filter(session =>
            session.id !== TRAINEE_AND_EARLY_CAREER_DINNER_SESSION_ID
            || isAdminSection
            || hasTraineeAndEarlyCareerDay);
    }, [calculatedAdditionalSessions, hasTraineeAndEarlyCareerDay, isAdminSection]);

    if (additionalSessionsLoading)
    {
        return (
            <>
                <Loading />
            </>
        )
    }

    return (
        <>
            {
                includedSessionsUpdated && (
                    <SpeakerSessionGroup 
                        title="Sessions:"
                        sessions={convertToSessionList(includedSessionsUpdated, selectedSessions, initialSessions)}
                        included
                        hideAdditionalAttendees={true}
                        updateSelectedSessions={updateSelectedSessions}
                        isSpeaker={isSpeaker}
                        isCap={isCap}
                        isTAndECSpeaker={isTAndECSpeaker}
                    />
                )
            }
            {
                includedOptionalSessions && Object
                    .keys(includedOptionalSessions)
                    .map((key, i) => {
                        return (
                            <SpeakerSessionGroup 
                                key={key}
                                title="Select between the following:"
                                sessions={convertToSessionList(includedOptionalSessions[key], selectedSessions, initialSessions)}
                                included
                                optional
                                updateSelectedSessions={updateSelectedSessions}
                                isSpeaker={isSpeaker}
                                isCap={isCap}
                                isTAndECSpeaker={isTAndECSpeaker}

                            />
                        )
                    })
            }
            {
                additionalSessionsUpdated && additionalSessionsUpdated.length !== 0 && (
                    <SpeakerSessionGroup 
                        title="Please select any additional sessions:"
                        sessions={convertToSessionList(additionalSessionsUpdated, selectedSessions, initialSessions)}
                        updateSelectedSessions={updateSelectedSessions}
                        isSpeaker={isSpeaker}
                        isCap={isCap}
                        isTAndECSpeaker={isTAndECSpeaker}
                    />
                )
            }
            {
                optionalSessions && Object
                    .keys(optionalSessions)
                    .map((key, i) => {
                        return (
                            <SpeakerSessionGroup 
                                key={key}
                                title={"Select between the following additional sessions (Optional):"}
                                sessions={convertToSessionList(optionalSessions[key], selectedSessions, initialSessions)}
                                included
                                optional
                                updateSelectedSessions={updateSelectedSessions}
                                isSpeaker={isSpeaker}
                                isCap={isCap}
                                isTAndECSpeaker={isTAndECSpeaker}
                            />
                        )
                    })
            }
        </>
    );
}

export default SpeakerPackageSessions;
